import { setSeconds } from "date-fns";
import { useEffect, useMemo } from "react";
import { useMap } from "react-map-gl/maplibre";
import { useApiResource } from "../../../../hooks/useApiResource";
import { IFlightMarker } from "../../../../models/airports/AirportConfig";
import {
  AIRPORT_CODE_PARAMETER,
  ICAO_ADDRESS,
} from "../../../../utils/common/Constants";
import {
  convertToUTC,
  formatDateForApiPayload,
} from "../../../../utils/common/DateHelperFunctions";
import FlightPathLayer from "../../layers/FlightPathLayer";
import { isValidLatLng, toLngLat } from "../../utils/coordinates";
import FlightMarker from "./FlightMarker";

export interface SelectedFlightMarkerProps {
  selectedFlightMarkerDetails: IFlightMarker;
  airportCode: string;
  handleMarkerClick: (flightID: string, docId: string) => void;
}

const flightTrackingPathURL = process.env.REACT_APP_API_FLIGHT_TRACKING_PATH;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const flightTrackingPathUrl = `${baseURL}${flightTrackingPathURL}`;

const SelectedFlightMarker: React.FC<SelectedFlightMarkerProps> = ({
  selectedFlightMarkerDetails,
  airportCode,
  handleMarkerClick,
}) => {
  const { current: map } = useMap();
  const trackingUrlWithParams = selectedFlightMarkerDetails?.id
    ? flightTrackingPathUrl
        ?.replace(AIRPORT_CODE_PARAMETER, airportCode)
        .replace(ICAO_ADDRESS, selectedFlightMarkerDetails?.id)
    : "";

  const payload = {
    end: formatDateForApiPayload(setSeconds(convertToUTC(new Date()), 0)),
    start: selectedFlightMarkerDetails.bestOffBlockTime,
    callSign: selectedFlightMarkerDetails.callSign,
    flightNumber: selectedFlightMarkerDetails.flightNumber,
    limit: 500,
  };

  const { data: flightTrackingData, isLoading } = useApiResource({
    url: trackingUrlWithParams,
    queryKey: [
      "maplibre-airport-tracking",
      airportCode,
      JSON.stringify(selectedFlightMarkerDetails),
    ],
    fetchOnPost: true,
    fetchPayload: payload,
  });

  const flightPath: [number, number][] = useMemo(
    () =>
      flightTrackingData?.data_points
        ?.map(
          (point: any) =>
            [Number(point.latitude), Number(point.longitude)] as [
              number,
              number,
            ]
        )
        .filter(isValidLatLng) ?? [],
    [flightTrackingData]
  );
  const reportedPosition: [number, number] = useMemo(
    () => [
      Number(selectedFlightMarkerDetails.latitude),
      Number(selectedFlightMarkerDetails.longitude),
    ],
    [
      selectedFlightMarkerDetails.latitude,
      selectedFlightMarkerDetails.longitude,
    ]
  );
  const flightPosition = isLoading
    ? reportedPosition
    : (flightPath.at(-1) ?? reportedPosition);

  useEffect(() => {
    if (!map || !isValidLatLng(flightPosition)) {
      return;
    }
    map.flyTo({
      center: toLngLat(flightPosition),
      zoom: map.getZoom(),
      duration: 1000,
    });
  }, [flightPosition[0], flightPosition[1], map]);

  return (
    <>
      {!isLoading && (
        <FlightPathLayer
          flightId={selectedFlightMarkerDetails.id}
          path={flightPath}
        />
      )}
      <FlightMarker
        airportCode={airportCode}
        flightNumber={selectedFlightMarkerDetails.flightNumber}
        direction={selectedFlightMarkerDetails.direction}
        position={flightPosition}
        flightStatus={selectedFlightMarkerDetails.status}
        legType={selectedFlightMarkerDetails.legType}
        docID={selectedFlightMarkerDetails.docId}
        selectedFlight
        acType={selectedFlightMarkerDetails.aircraftType}
        acReg={selectedFlightMarkerDetails.aircraftRegistration}
        onBlockTime={selectedFlightMarkerDetails.bestInBlockTime}
        offBlockTime={selectedFlightMarkerDetails.bestOffBlockTime}
        updateMarkerId={handleMarkerClick}
      />
    </>
  );
};

export default SelectedFlightMarker;
